import { EventType } from "@/analytics/analytics-events";
import {
  computeToolPropertyForSelectLocationEvent,
  SelectLocationProperties,
  SelectLocationSources,
} from "@/analytics/generic-events-properties";
import { WaypointLabelRender } from "@/components/r3f/renderers/waypoint-label-render";
import {
  useOffsetPlaceholders,
  useVisiblePlaceholders,
  useWaypoints,
} from "@/hooks/use-placeholders";
import { useWalkPlaceholderPositions } from "@/hooks/use-walk-placeholder-positions";
import { usePickClosestWaypoint } from "@/modes/walk-mode/hooks/use-pick-closest-waypoints";
import { useAppSelector } from "@/store/store-hooks";
import { ToolName } from "@/store/ui/ui-slice";
import {
  selectObjectVisibility,
  selectShouldShowWaypointsOnFloors,
  selectVisibilityDistance,
} from "@/store/view-options/view-options-selectors";
import { ViewObjectTypes } from "@/store/view-options/view-options-slice";
import { OverviewWaypointsRenderer } from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  IElementGenericImgSheet,
  IElementImg360,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { JSX, useCallback, useMemo } from "react";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";

type FlyWalkWaypointsRendererProps = {
  /** List of placeholders to show */
  panos: IElementImg360[];
  /** The active pano */
  activePano?: IElementImg360;
  /** Callback issued when a pano is clicked */
  onPanoClicked(pano: IElementImg360): void;
  /** The sheet element whose elevation should be used for 2D elements */
  sheetForElevation?: IElementGenericImgSheet;
  /** Whether the waypoints should be visible */
  waypointsVisible: boolean;
  /** Whether the rendered waypoints should fade off with distance */
  fadeOff: boolean;
};

/** @returns a component to render waypoints in flywalk mode */
export function FlyWalkWaypointsRenderer({
  panos,
  activePano,
  onPanoClicked,
  sheetForElevation,
  waypointsVisible,
  fadeOff,
}: FlyWalkWaypointsRendererProps): JSX.Element | null {
  const shouldShowWaypointsOnFloor = useAppSelector(
    selectShouldShowWaypointsOnFloors,
  );

  const positions = useWalkPlaceholderPositions(
    panos,
    sheetForElevation,
    !shouldShowWaypointsOnFloor,
  );

  const { clippingPlanes, setWaypointHovered, activeToolAndPicking } =
    useToolsAcquaintance();

  const { visiblePlaceholders, visiblePositions } = useVisiblePlaceholders({
    placeholders: panos,
    positions,
    clippingPlanes,
  });

  const { placeholdersOffset, shiftedPlaceholders } =
    useOffsetPlaceholders(visiblePositions);

  const waypoints = useWaypoints(visiblePlaceholders, visiblePositions);

  const shouldWayPointsBeVisible = useAppSelector(
    selectObjectVisibility(ViewObjectTypes.waypoints),
  );

  const shouldDisplayWaypointLabels = useAppSelector(
    selectObjectVisibility(ViewObjectTypes.waypointLabels),
  );

  const onWaypointClicked = useCallback(
    (index: number) => {
      Analytics.track<SelectLocationProperties>(EventType.selectLocation, {
        via: SelectLocationSources.model3dView,
        tool: computeToolPropertyForSelectLocationEvent(
          activeToolAndPicking.activeTool,
        ),
      });
      if (isIElementImg360(visiblePlaceholders[index])) {
        onPanoClicked(visiblePlaceholders[index]);
      }
    },
    [activeToolAndPicking.activeTool, visiblePlaceholders, onPanoClicked],
  );

  const onWaypointHovered = useCallback(
    (id: number | undefined) => {
      setWaypointHovered(id !== undefined);
    },
    [setWaypointHovered],
  );

  const camera = useThree((s) => s.camera);

  const hoveredPano = usePickClosestWaypoint({
    camera,
    sheetForElevation,
    waypoints,
    activePano: waypointsVisible ? activePano : undefined,
    onWaypointClicked: onPanoClicked,
  });

  const hoveredId = useMemo(() => {
    if (!hoveredPano) return;
    for (let i = 0; i < visiblePlaceholders.length; i++) {
      if (visiblePlaceholders[i].id === hoveredPano.id) {
        return i;
      }
    }
  }, [hoveredPano, visiblePlaceholders]);

  const visibilityDistance = useAppSelector(selectVisibilityDistance);

  if (
    (activeToolAndPicking.isPickingToolActive &&
      activeToolAndPicking.activeTool !== ToolName.measurement) ||
    !waypointsVisible
  ) {
    return null;
  }

  return (
    <>
      <group position={placeholdersOffset}>
        <OverviewWaypointsRenderer
          visible={shouldWayPointsBeVisible}
          waypoints={shiftedPlaceholders}
          shouldFaceCamera={!shouldShowWaypointsOnFloor}
          hoveredId={hoveredId}
          fadeOffDistance={fadeOff ? visibilityDistance : undefined}
          onWaypointClicked={onWaypointClicked}
          onWaypointHovered={onWaypointHovered}
        />
      </group>
      {
        // Render waypoint labels
        shouldWayPointsBeVisible && shouldDisplayWaypointLabels && (
          <WaypointLabelRender
            waypoints={waypoints}
            onLabelClick={onPanoClicked}
          />
        )
      }
    </>
  );
}
